import type { HeroProps } from '@/components/home/Hero';
import type {
  CustomersReviewsProps,
  GenericProductsProps,
  HowItWorksProps,
  InstantEverythingProps,
  ProductsProps,
  TrustProps,
} from '@/components/shared';
import type { BasePageConfig } from '@/pageConfig/BasePageConfig';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import room from '@/static/assets/home/living-room-with-checkmarks.svg';
import nyse from '@/static/assets/home/nyse.png';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_nl-NL.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_3.svg';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  products: ProductsProps;
  genericProducts: GenericProductsProps;
  instantEverything: InstantEverythingProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
}

export const config: Config = {
  seo: { ...sharedConfig['nl-NL'].seo },
  layout: {
    ...sharedConfig['nl-NL'].layout,
    showAskUsAnything: true,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['nl-NL'].layout.headerActions,
      CTAs: {
        ...sharedConfig['nl-NL'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['nl-NL'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Bekijk onze prijzen',
            plural: 'Bekijk onze prijzen',
          },
        },
      },
    },
  },
  hero: {
    title: 'Vergeet alles wat je weet\nover verzekeringen',
    subtitle: 'Direct geregeld. Scherpe prijzen. Groot hart.',
    ctaLabel: 'Bekijk onze prijzen',
    cart: {
      singular: 'offerte',
      plural: 'Terug naar je offerte',
    },
  },
  customersReviews: {
    darkBackground: true,
    title: 'De (Bijna) 5 Sterren Verzekeraar',
    subtitle: 'Lemonade heeft een App store rating van 4.9',
  },
  products: {
    title: 'Geweldige prijzen. Geen papierwerk. Op elk moment opzeggen.',
    trackingLabel: 'home',
    layout: {
      mt: [-10, -50],
    },
    cardsWidth: {
      desktop: 300,
    },
    products: [
      {
        title: 'Inboedel',
        subtitle: 'Het ultieme vangnet voor jou en de spullen waar je van houdt',
        price: 'Vanaf €5 per maand',
        product: 'home_uk_contents',
        ctaLabelDesktop: 'Bekijk onze prijzen',
        ctaLabelMobile: 'Bekijk prijzen',
        alt: 'Inboedel',
        absoluteUrl: `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/nl/home/quote?f=1&productCoverage=contents_and_liability`,
      },
      {
        title: 'Opstal',
        subtitle: 'Beschermt je huis tegen allerlei soorten schade en gevaren',
        price: 'Vanaf €17 per maand',
        product: 'home_uk_buildings',
        ctaLabelDesktop: 'Bekijk onze prijzen',
        ctaLabelMobile: 'Bekijk prijzen',
        alt: 'Opstal',
        absoluteUrl: `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/nl/home/quote?f=1&productCoverage=buildings_and_liability`,
      },
      {
        title: 'Inboedel & Opstal',
        subtitle: 'Beschermt jou en je huis tegen allerlei vervelende situaties',
        price: 'Vanaf €21 per maand',
        product: 'home_uk_buildings_contents',
        ctaLabelDesktop: 'Bekijk onze prijzen',
        ctaLabelMobile: 'Bekijk prijzen',
        alt: 'Inboedel & Opstal',
        absoluteUrl: `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/nl/home/quote?f=1&productCoverage=buildings_contents_and_liability`,
      },
    ],
  },
  genericProducts: {
    title: 'Woon- en aansprakelijkheidsverzekering\nvoor de 21e eeuw',
    subtitle: 'Best beoordeelde bescherming voor jou, je spullen en je huis',
    bgImage: room.src,
  },
  instantEverything: {
    title: 'Direct geregeld',
    subtitle:
      'Maya, onze charmante bot, gebruikt kunstmatige intelligentie om de meest passende\nverzekering voor jou te vinden. Super eenvoudig en snel!',
    appSketchContentLeft: 'Verzekerd in\n<b>seconden</b>',
    appSketchContentRight: 'Uitbetaald\n<b>in minuten</b>',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        left: { lg: '-435px' },
      },
      contentSectionRight: {
        right: { lg: '-415px' },
      },
    },
    layout: {
      mt: [-30, -50],
    },
  },
  howItWorks: {
    darkBackground: false,
    title: 'Hoe Lemonade werkt',
    showBrandsImage: false,
    subtitle: `Lemonade heeft een ander verzekeringsmodel dan de traditionele verzekeraars. Wij vinden dat de premie die jij betaalt jouw geld is en niet dat van ons. Lemonade is eenvoudig te begrijpen en transparant. Wij nemen een vast percentage van de premie, betalen schadevergoedingen super snel en <a aria-labelledby="how-it-works-subtitle" href="/nl/giveback">doneren</a> het overgebleven geld aan een goed doel waar jij voor kiest!*`,
    disclaimer: `* Afhankelijk van de goedkeuring van de Raad van Bestuur en het voldoen aan de gestelde financiële verplichtingen. <a aria-labelledby="how-it-works-disclaimer" href="/nl/faq#giveback">Lees hier meer</a>`,
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'Een <b>vast percentage</b> </br> om het bedrijf te <br/>kunnen runnen',
      },
      {
        image: pizza.mobile[2].src,
        text: 'Claims worden<br/> <b>super snel</b> uitbetaald',
      },
      {
        image: pizza.mobile[3].src,
        text: `Als er geld overblijft,<br/> <b>doneren we dat bedrag <br/> aan een goed doel</br>`,
      },
    ],
  },
  trust: {
    bulletsLayout: 'horizontal',
    bullets: [
      {
        name: 'social',
        title: 'Ontwikkeld om een sociale impact te maken',
        text: 'Lemonade Inc. is een Public Benefit Corporation en gecertificeerde B-Corp. Sociale impact is een onderdeel van onze missie en businessmodel - geen marketing kletskoek',
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'nyse',
        title: 'NYSE:LMND',
        text: 'Lemonade is een beursgenoteerd bedrijf, genoteerd aan de New York Stock Exchange onder het beurs symbool LMND',
        image: {
          src: nyse,
          width: 100,
          height: 89,
        },
      },
      {
        name: 'rating',
        title: 'Best beoordeeld en ondersteund door marktleiders',
        text: 'Lemonade Inc. is als beste beoordeeld heeft een licentie en wordt herverzekerd door de meest gevestigde grote namen in de markt',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
};
